/**
 * Consultation experience — type definitions + one static UI lookup.
 *
 * Contains:
 *   • TYPES used to type-check the consultation payload + its consumers
 *     (bio-age values, augmented system shape, metric tiles, body comp,
 *     energy training, test history).
 *   • `METRIC_TILE_SYSTEMS` — static UI lookup mapping each Section-2 tile
 *     to the sub-steps it highlights when active. Lives alongside the
 *     `MetricTileKey` type it's keyed by.
 */

import {
  dataPlatformServerObjectType,
  systemGroupType,
} from "../../../providers/dataPlatformReportTypes";

// --- Bio-age value types ----------------------------------------------------

/**
 * One biological-age measurement at a given test date. Used for both the
 * current bio age (`bioAge`) and any past tests in `bioAgeHistory`.
 * `reportDate` is an ISO date string.
 */
export type BioAgeMeasurement = {
  biological: number;
  chronological: number;
  reportDate: string;
};

/**
 * Predicted reduction after following the prescribed plan (Section 3 header).
 * Deferred for v1 — backend doesn't ship this yet pending Product on the
 * scoring model.
 */
export type BioAgeProjectionBlock = {
  targetBioAge: number;
  weeksToTarget: number;
  totalImprovementYears: number;
};

// --- Augmented platform-data shape -----------------------------------------
//
// The upstream `systemGroupType` is stale relative to what the backend now
// ships per consultation system. These augmented types layer the extra
// fields on so consumers can read them with proper typing. Once
// `dataPlatformReportTypes.ts` is updated to include these fields natively
// the augmentation can be deleted.

export type AugmentedSystemGroup = systemGroupType & {
  /** Bio-age impact flag from backend: 1 = younger, 0 = not classified,
   *  -1 = older. */
  bioAgeContribution: -1 | 0 | 1;
  /** Per-system "What you are likely experiencing" copy. */
  likelyExperiencing: { label: string; description: string }[];
  /** Backend ships `{ positive, negative }` of `{label, description?}` objects. */
  influencedBy: FactorBlock;
  /** Same shape as `influencedBy`. */
  affectsThis: FactorBlock;
};

export type AugmentedPlatformData = Omit<
  dataPlatformServerObjectType,
  "score"
> & {
  score: dataPlatformServerObjectType["score"] & {
    systems: AugmentedSystemGroup[];
  };
};

// --- Recommendations / factors copy ----------------------------------------

/**
 * "What you are likely experiencing" copy per system. Shape is
 * `{ label, description }[]` — one row per likely-experience headline.
 */
export type LikelyExperiencingItem = { label: string; description: string };

/**
 * "Influenced by" / "Affects this" runtime shape (the static type in
 * `dataPlatformReportTypes` is stale). Both sides ship as positive/negative
 * factor lists. `description` is optional — the consultation chips are
 * read-only and never reveal a popup, but the score-screen path authors
 * real descriptions on the same shape.
 */
export type FactorItem = { label: string; description?: string };
export type FactorBlock = { positive: FactorItem[]; negative: FactorItem[] };

// --- Section-2 main metrics tiles ------------------------------------------

/**
 * Identifiers for the 5 KPI tiles rendered in the Section-2 top row.
 * Used as keys for both the value payload (`MainMetricsTiles`) and the
 * system-highlight lookup (`METRIC_TILE_SYSTEMS`).
 */
export type MetricTileKey = "muscle" | "rmr" | "vo2" | "bodyfat" | "fatcarb";

/** Shape of one of the four single-value Section-2 metric tiles. */
export type MetricTileValue = {
  value: string | number;
  unit: string;
  score: number;
  scoreCategory: number;
};

/** Shape of the Section-2 "Fat / Carb burn" tile — a two-color stacked bar
 *  rather than a single value. The backend ships the split it wants to
 *  display in the headline KPI (e.g. derived from a base aerobic zone, a
 *  weighted average across zones, or a user-specific reference point —
 *  the frontend just renders). */
export type FatCarbTileValue = {
  fatPct: number;
  carbPct: number;
};

/** All 5 Section-2 KPI tiles. Heterogeneous shape — 4 single-value tiles
 *  + 1 fat/carb split tile. */
export type MainMetricsTiles = {
  muscle: MetricTileValue;
  rmr: MetricTileValue;
  vo2: MetricTileValue;
  bodyfat: MetricTileValue;
  fatcarb: FatCarbTileValue;
};

/**
 * Which Section-2 metric tile (Muscle Mass, RMR, VO2max, Body Fat, Fat/Carb
 * burn) belongs to which sidebar system. Drives the "selected" highlight on
 * the top metrics row when a system is active. Keys are tile ids; values are
 * sub-step ids from `steps.ts` (`heart`, `lungs`, `body-composition`, …,
 * `energy-training`).
 *
 * Source of truth: "Consultation experience feature Spec" doc §5.1 →
 * "Connections between systems and metrics" table.
 */
export const METRIC_TILE_SYSTEMS: Record<MetricTileKey, string[]> = {
  rmr: ["metabolism", "mitochondria", "body-composition"],
  vo2: ["heart", "lungs", "respiratory", "mitochondria", "energy-training"],
  bodyfat: ["body-composition", "heart"],
  muscle: ["body-composition", "metabolism"],
  fatcarb: ["metabolism", "mitochondria", "body-composition", "energy-training"],
};

// --- Body composition (InBody segmental) -----------------------------------

/**
 * InBody segmental body-composition data, used by the Body Composition
 * system's right-hand body figure (Figma node 14725:50370). Each top-level
 * tab (Lean, Fat, ECW/TBW, ECW, ICW, TBW) ships values for the 5 body
 * segments (left/right arm, trunk, left/right leg).
 */
export type BodyCompositionSegmentId =
  | "leftArm"
  | "rightArm"
  | "trunk"
  | "leftLeg"
  | "rightLeg";

export type BodyCompositionSegment = {
  value: string; // formatted with unit, e.g. "2.8 lbs/kgs"
  percentage: string; // formatted, e.g. "14%"
};

export type BodyCompositionTabId =
  | "lean"
  | "fat"
  | "ecw_tbw"
  | "ecw"
  | "icw"
  | "tbw";

export type BodyCompositionTab = {
  id: BodyCompositionTabId;
  label: string;
  segments: Record<BodyCompositionSegmentId, BodyCompositionSegment>;
};

export type BodyCompositionData = {
  deviceName: string;
  tabs: BodyCompositionTab[];
};

// --- Energy & Training zone data -------------------------------------------

/**
 * Per-zone HR / watts / speed ranges plus the fat/carb fuel split as
 * percentages (`fat`, `carb` — sum to 100) and the gram-per-minute burn
 * rates derived from gas exchange during the breath test. Total kcal/min
 * is derived client-side as `fatGrams * 9 + carbGrams * 4` (standard
 * nutrition conversion) — see CONSULTATION_BACKEND_FIXES_V1.md #9.
 *
 * A range the test did not measure arrives as `{ lower: 0, upper: 0 }` or as
 * `null` (dev, 2026-10-29: Kouzoulos gets `watts: null, speed: null`).
 */
export type EnergyTrainingZoneRange = { lower: number; upper: number } | null;

export type EnergyTrainingZone = {
  zone: number;
  hr: EnergyTrainingZoneRange;
  watts: EnergyTrainingZoneRange;
  speed: EnergyTrainingZoneRange;
  fat: number; // % of zone energy from fat (0–100)
  carb: number; // % of zone energy from carbs (0–100)
  fatGrams: number; // grams/min from fat
  carbGrams: number; // grams/min from carbs
};

export type EnergyTrainingData = {
  zones: EnergyTrainingZone[];
};

// Note: the Section 2 historic-comparison view (`TestHistoryTable` +
// `BundleBiomarker`/`BundleMetric`/`BundleSystem`/`BundleSnapshot`/`TestHistory`)
// was removed per product. When the feature is rebuilt the bundle types
// can be reintroduced here (or replaced by whatever shape the redesigned
// backend payload uses).
